// Leads frios (prospects): column types, import inference and validation.
// Pure module (no Deno/URL imports) shared by the admin UI (import dialog,
// cell editors), the REST `api` and the `mcp` functions — one rule for every
// way data enters `prospects.data`. Spec: docs/specs/flows/prospeccao.md.

export const PROSPECT_FIELD_TYPES = [
  "text",
  "long_text",
  "number",
  "currency",
  "url",
  "email",
  "phone",
  "date",
  "boolean",
  "select",
  "multi_select",
  "rating",
] as const;
export type ProspectFieldType = (typeof PROSPECT_FIELD_TYPES)[number];

/** `promoted` is set only by the promote_prospect RPC, never by import/edit. */
export const PROSPECT_STATUSES = [
  "new",
  "researching",
  "qualified",
  "discarded",
  "promoted",
] as const;
export type ProspectStatus = (typeof PROSPECT_STATUSES)[number];

export const OPTION_TONES = [
  "neutral",
  "primary",
  "info",
  "ok",
  "warn",
  "notice",
  "bad",
] as const;
export type OptionTone = (typeof OPTION_TONES)[number];

export interface SelectOption {
  value: string;
  label: string;
  tone?: OptionTone;
}

export interface ProspectFieldDef {
  key: string;
  label: string;
  type: ProspectFieldType;
  options: SelectOption[];
}

export type ProspectCoreKey =
  | "name"
  | "contact_name"
  | "email"
  | "phone"
  | "website"
  | "instagram"
  | "city"
  | "state"
  | "notes"
  | "tags"
  | "status"
  | "external_id";

export interface CoreFieldDef {
  key: ProspectCoreKey;
  label: string;
  type: ProspectFieldType;
  /** Normalized header names (see normalizeHeader) that map to this field. */
  aliases: readonly string[];
}

/** Columns every list has. Their keys can never be used by a custom column. */
export const PROSPECT_CORE_FIELDS: readonly CoreFieldDef[] = [
  {
    key: "name",
    label: "Nome",
    type: "text",
    aliases: [
      "name",
      "nome",
      "empresa",
      "company",
      "razao social",
      "nome fantasia",
      "comercio",
      "estabelecimento",
      "negocio",
      "title",
      "titulo",
    ],
  },
  {
    key: "contact_name",
    label: "Contato",
    type: "text",
    aliases: ["contato", "contact", "contact name", "responsavel", "pessoa"],
  },
  {
    key: "email",
    label: "E-mail",
    type: "email",
    aliases: ["email", "e mail", "mail"],
  },
  {
    key: "phone",
    label: "Telefone",
    type: "phone",
    aliases: [
      "phone",
      "telefone",
      "fone",
      "celular",
      "whatsapp",
      "tel",
      "phone number",
    ],
  },
  {
    key: "website",
    label: "Site",
    type: "url",
    aliases: ["site", "website", "web", "url", "pagina", "dominio"],
  },
  {
    key: "instagram",
    label: "Instagram",
    type: "url",
    aliases: ["instagram", "insta", "ig", "redes sociais", "rede social"],
  },
  {
    key: "city",
    label: "Cidade",
    type: "text",
    aliases: ["city", "cidade", "municipio"],
  },
  {
    key: "state",
    label: "UF",
    type: "text",
    aliases: ["state", "uf", "estado"],
  },
  {
    key: "notes",
    label: "Observações",
    type: "long_text",
    aliases: ["notes", "observacao", "observacoes", "obs", "notas", "nota"],
  },
  {
    key: "tags",
    label: "Tags",
    type: "multi_select",
    aliases: ["tags", "etiquetas", "tag"],
  },
  {
    key: "status",
    label: "Status",
    type: "select",
    aliases: ["status", "situacao"],
  },
  {
    key: "external_id",
    label: "ID externo",
    type: "text",
    aliases: [
      "external id",
      "id externo",
      "leads id",
      "lead id",
      "place id",
      "placeid",
      "id",
    ],
  },
];

export const PROSPECT_CORE_KEYS: ReadonlySet<string> = new Set(
  PROSPECT_CORE_FIELDS.map((f) => f.key),
);

/** pt-BR words people use for each status in spreadsheets. */
const STATUS_ALIASES: Record<string, ProspectStatus> = {
  new: "new",
  novo: "new",
  nova: "new",
  researching: "researching",
  pesquisando: "researching",
  "em pesquisa": "researching",
  qualified: "qualified",
  qualificado: "qualified",
  qualificada: "qualified",
  discarded: "discarded",
  descartado: "discarded",
  descartada: "discarded",
};

export const MAX_IMPORT_ROWS = 500;
/** Distinct source columns accepted in one import. */
export const MAX_IMPORT_COLUMNS = 100;
/** New custom columns one import may create (the DB caps a list at 150). */
export const MAX_NEW_FIELDS_PER_IMPORT = 50;
export const MAX_FIELDS_PER_LIST = 150;
const MAX_TEXT = 5000;
const MAX_SELECT_OPTIONS = 100;
const MAX_OPTION_LENGTH = 80;
const MAX_MULTI_VALUES = 30;
const MAX_TAGS = 30;
const MAX_TAG_LENGTH = 60;

// ---------------------------------------------------------------- strings

/** Lower case, no accents, separators collapsed to one space. */
export function normalizeHeader(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\(.*?\)/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** Column label → stable snake_case key (`Score (0-100)` → `score`). */
export function slugifyKey(label: string): string {
  let key = normalizeHeader(label).replace(/ /g, "_").slice(0, 63);
  if (!key) key = "coluna";
  if (!/^[a-z]/.test(key)) key = `c_${key}`.slice(0, 63);
  return key;
}

/** `key`, `key_2`, `key_3`… avoiding core keys and the ones already taken. */
export function uniqueKey(base: string, taken: ReadonlySet<string>): string {
  let key = base;
  let n = 2;
  while (taken.has(key) || PROSPECT_CORE_KEYS.has(key)) {
    const suffix = `_${n++}`;
    key = `${base.slice(0, 63 - suffix.length)}${suffix}`;
  }
  return key;
}

/** The PostgREST `or=` filter for a free-text search (mirror of src/lib/searchFilter.ts). */
export function ilikeAnyFilter(columns: string[], term: string): string {
  const pattern = term.replace(/[\\%_]/g, (c) => `\\${c}`).replace(
    /"/g,
    '\\"',
  );
  return columns.map((col) => `${col}.ilike."%${pattern}%"`).join(",");
}

// ---------------------------------------------------------------- parsers

const EMPTY = new Set(["", "-", "—", "–", "n/a", "na", "null", "undefined"]);

function isBlank(raw: unknown): boolean {
  return raw === null || raw === undefined ||
    (typeof raw === "string" && EMPTY.has(raw.trim().toLowerCase()));
}

/** Absolute http(s) URL (adds https:// to a bare domain), else null. */
export function normalizeUrl(raw: string): string | null {
  let value = raw.trim().replace(/\\_/g, "_").split(/\s+/)[0] ?? "";
  if (!value) return null;
  if (!/^[a-z][a-z0-9+.-]*:/i.test(value)) {
    if (!/^[\w-]+(\.[\w-]+)+(\/|$)/.test(value)) return null;
    value = `https://${value}`;
  }
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:"
      ? url.href
      : null;
  } catch {
    return null;
  }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** "1.200,50", "R$ 1.200", "1,200.50", "85%" → number. */
export function parseNumberBR(raw: unknown): number | null {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  if (typeof raw !== "string") return null;
  let s = raw.trim().replace(/^r\$\s*/i, "").replace(/[%\s]/g, "");
  if (!/^-?[\d.,]+$/.test(s)) return null;
  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  if (lastComma > lastDot) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (lastDot > lastComma && lastComma !== -1) {
    s = s.replace(/,/g, "");
  } else if (lastComma === -1 && (s.match(/\./g) ?? []).length > 1) {
    s = s.replace(/\./g, "");
  } else if (lastComma === -1 && /^\d{1,3}\.\d{3}$/.test(s)) {
    // "1.200" in a pt-BR sheet is one thousand two hundred.
    s = s.replace(".", "");
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function validYmd(y: number, m: number, d: number): string | null {
  const date = new Date(Date.UTC(y, m - 1, d));
  if (
    date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 ||
    date.getUTCDate() !== d
  ) return null;
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${
    String(d).padStart(2, "0")
  }`;
}

/** dd/mm/aaaa, dd-mm-aaaa, aaaa-mm-dd (with or without time) → "aaaa-mm-dd". */
export function parseDateBR(raw: unknown): string | null {
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return raw.toISOString().slice(0, 10);
  }
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/);
  if (m) return validYmd(+m[1], +m[2], +m[3]);
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/);
  if (m) {
    const year = m[3].length === 2 ? 2000 + +m[3] : +m[3];
    return validYmd(year, +m[2], +m[1]);
  }
  return null;
}

const TRUE_WORDS = new Set([
  "sim",
  "s",
  "yes",
  "y",
  "true",
  "1",
  "x",
  "verdadeiro",
]);
const FALSE_WORDS = new Set(["nao", "n", "no", "false", "0", "falso"]);

export function parseBoolean(raw: unknown): boolean | null {
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "number") {
    return raw === 1 ? true : raw === 0 ? false : null;
  }
  if (typeof raw !== "string") return null;
  const v = normalizeHeader(raw);
  if (TRUE_WORDS.has(v)) return true;
  if (FALSE_WORDS.has(v)) return false;
  return null;
}

/** "a; b, c" | ["a","b"] → ["a","b","c"] (trimmed, unique, non-empty). */
export function splitList(raw: unknown): string[] {
  const parts = Array.isArray(raw)
    ? raw.map((v) => (typeof v === "string" ? v : String(v)))
    : typeof raw === "string"
    ? raw.split(/[;,\n|]/)
    : [];
  return [...new Set(parts.map((p) => p.trim()).filter((p) => !isBlank(p)))];
}

function asText(raw: unknown): string {
  if (typeof raw === "string") return raw.trim();
  if (typeof raw === "number" || typeof raw === "boolean") return String(raw);
  if (raw instanceof Date) return raw.toISOString();
  return JSON.stringify(raw);
}

// ---------------------------------------------------------------- coercion

// `error?: undefined` / `value?: undefined` keep the union readable from the
// app's tsconfig (strict off → no narrowing on `ok`).
export type CoerceResult =
  | { ok: true; value: unknown; error?: undefined }
  | { ok: false; error: string; value?: undefined };

/**
 * Raw spreadsheet/JSON value → the canonical value stored in `data` for a
 * column type. Blank → `{ ok: true, value: null }` (the key is dropped).
 * Select values are matched to an option by value or label, case/accents
 * ignored; an unknown one is kept as typed (`ensureOptions` adds it).
 */
export function coerceValue(
  field: Pick<ProspectFieldDef, "type" | "options">,
  raw: unknown,
): CoerceResult {
  if (isBlank(raw)) return { ok: true, value: null };
  switch (field.type) {
    case "text":
    case "long_text": {
      const text = asText(raw);
      return text.length > MAX_TEXT
        ? { ok: false, error: `máximo ${MAX_TEXT} caracteres` }
        : { ok: true, value: text };
    }
    case "number":
    case "currency": {
      const n = parseNumberBR(raw);
      return n === null
        ? { ok: false, error: "número inválido" }
        : { ok: true, value: n };
    }
    case "rating": {
      const n = parseNumberBR(raw);
      if (n === null || n < 0 || n > 5) {
        return { ok: false, error: "avaliação de 0 a 5" };
      }
      return { ok: true, value: Math.round(n) };
    }
    case "url": {
      const url = normalizeUrl(asText(raw));
      return url
        ? { ok: true, value: url }
        : { ok: false, error: "link inválido (use http/https)" };
    }
    case "email": {
      const email = asText(raw).toLowerCase();
      return EMAIL_RE.test(email)
        ? { ok: true, value: email }
        : { ok: false, error: "e-mail inválido" };
    }
    case "phone": {
      const phone = asText(raw);
      return /\d{6,}/.test(phone.replace(/\D/g, ""))
        ? { ok: true, value: phone }
        : { ok: false, error: "telefone inválido" };
    }
    case "date": {
      const date = parseDateBR(raw);
      return date
        ? { ok: true, value: date }
        : { ok: false, error: "data inválida" };
    }
    case "boolean": {
      const b = parseBoolean(raw);
      return b === null
        ? { ok: false, error: "use sim ou não" }
        : { ok: true, value: b };
    }
    case "select":
      return { ok: true, value: matchOption(field.options, asText(raw)) };
    case "multi_select": {
      const values = splitList(raw);
      return values.length > MAX_MULTI_VALUES
        ? { ok: false, error: `máximo ${MAX_MULTI_VALUES} valores` }
        : { ok: true, value: values.map((v) => matchOption(field.options, v)) };
    }
  }
}

function matchOption(options: SelectOption[], raw: string): string {
  const needle = normalizeHeader(raw);
  const hit = options.find((o) =>
    normalizeHeader(o.value) === needle || normalizeHeader(o.label) === needle
  );
  return hit ? hit.value : raw.trim().slice(0, MAX_OPTION_LENGTH);
}

/** Adds the values not yet in `options` (round-robin tones), capped. */
export function ensureOptions(
  options: SelectOption[],
  values: readonly string[],
): SelectOption[] {
  const out = [...options];
  const known = new Set(out.map((o) => normalizeHeader(o.value)));
  for (const value of values) {
    const norm = normalizeHeader(value);
    if (!norm || known.has(norm) || out.length >= MAX_SELECT_OPTIONS) continue;
    known.add(norm);
    out.push({
      value,
      label: value,
      tone: OPTION_TONES[out.length % OPTION_TONES.length],
    });
  }
  return out;
}

// ---------------------------------------------------------------- validation

/**
 * Checks values already in canonical form (what the UI/API/MCP sends to be
 * stored). Unknown keys are errors: a column must exist before it holds data.
 */
export function validateProspectData(
  fields: readonly ProspectFieldDef[],
  data: Record<string, unknown>,
  path = "data",
): string[] {
  const byKey = new Map(fields.map((f) => [f.key, f]));
  const errors: string[] = [];
  for (const [key, value] of Object.entries(data)) {
    const field = byKey.get(key);
    if (!field) {
      errors.push(`${path}.${key}: coluna não existe nesta lista`);
      continue;
    }
    if (value === null) continue;
    const result = coerceValue(field, value);
    if (!result.ok) {
      errors.push(`${path}.${key}: ${result.error}`);
      continue;
    }
    if (field.type === "select" || field.type === "multi_select") {
      const values = Array.isArray(result.value)
        ? result.value as string[]
        : [result.value as string];
      const allowed = new Set(field.options.map((o) => o.value));
      for (const v of values) {
        if (!allowed.has(v)) {
          errors.push(`${path}.${key}: opção "${v}" não existe`);
        }
      }
    }
  }
  return errors;
}

/** Normalizes values that passed validateProspectData (strings → canonical). */
export function normalizeProspectData(
  fields: readonly ProspectFieldDef[],
  data: Record<string, unknown>,
): Record<string, unknown> {
  const byKey = new Map(fields.map((f) => [f.key, f]));
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    const field = byKey.get(key);
    if (!field) continue;
    if (value === null) {
      out[key] = null;
      continue;
    }
    const result = coerceValue(field, value);
    if (result.ok) out[key] = result.value;
  }
  return out;
}

// ---------------------------------------------------------------- inference

/** Best column type for a sample of raw values (blank ones ignored). */
export function inferFieldType(samples: readonly unknown[]): ProspectFieldType {
  const values = samples.filter((v) => !isBlank(v));
  if (values.length === 0) return "text";
  const all = (pred: (v: unknown) => boolean) => values.every(pred);

  if (
    all((v) => typeof v === "boolean" || parseBoolean(v) !== null) &&
    values.some((v) => typeof v === "boolean" || !/^\d+$/.test(String(v)))
  ) {
    return "boolean";
  }
  if (all((v) => Array.isArray(v))) return "multi_select";
  const texts = values.map(asText);
  if (texts.every((t) => EMAIL_RE.test(t))) return "email";
  if (texts.every((t) => /^r\$/i.test(t) && parseNumberBR(t) !== null)) {
    return "currency";
  }
  if (all((v) => parseNumberBR(v) !== null)) return "number";
  if (all((v) => parseDateBR(v) !== null)) return "date";
  if (texts.every((t) => normalizeUrl(t) !== null && /[./]/.test(t))) {
    return "url";
  }
  if (
    texts.every((t) =>
      /^[\d\s()+.-]{8,}$/.test(t) && t.replace(/\D/g, "").length >= 8
    )
  ) return "phone";
  if (texts.some((t) => t.length > 120)) return "long_text";

  const distinct = new Set(texts.map(normalizeHeader));
  if (
    texts.every((t) => t.length <= 40) && distinct.size <= 12 &&
    distinct.size < texts.length
  ) return "select";
  return "text";
}

// ---------------------------------------------------------------- import

export type ImportTarget =
  | { kind: "core"; key: ProspectCoreKey }
  | {
    kind: "field";
    key: string;
    label: string;
    type: ProspectFieldType;
    isNew: boolean;
  }
  | { kind: "ignore" };

export interface ColumnPlan {
  /** Header / JSON key as it appears in the source. */
  source: string;
  target: ImportTarget;
}

function coreForHeader(header: string): ProspectCoreKey | null {
  const norm = normalizeHeader(header);
  const hit = PROSPECT_CORE_FIELDS.find((f) => f.aliases.includes(norm));
  return hit ? hit.key : null;
}

/**
 * Suggests where each source column goes: a core field (by alias, first
 * match wins), an existing custom column (same key or label), or a new
 * column with an inferred type (`autoCreate`), else ignored.
 */
export function suggestColumnPlan(
  headers: readonly string[],
  records: readonly Record<string, unknown>[],
  existing: readonly ProspectFieldDef[],
  autoCreate = true,
): ColumnPlan[] {
  const usedCore = new Set<ProspectCoreKey>();
  const taken = new Set(existing.map((f) => f.key));
  const sample = records.slice(0, 200);
  return headers.map((source) => {
    const core = coreForHeader(source);
    if (core && !usedCore.has(core)) {
      usedCore.add(core);
      return { source, target: { kind: "core", key: core } };
    }
    const norm = normalizeHeader(source);
    const slug = slugifyKey(source);
    const match = existing.find((f) =>
      f.key === slug || normalizeHeader(f.label) === norm
    );
    if (match) {
      return {
        source,
        target: {
          kind: "field",
          key: match.key,
          label: match.label,
          type: match.type,
          isNew: false,
        },
      };
    }
    if (!autoCreate) return { source, target: { kind: "ignore" } };
    const key = uniqueKey(slug, taken);
    taken.add(key);
    return {
      source,
      target: {
        kind: "field",
        key,
        label: source.trim().slice(0, 80) || key,
        type: inferFieldType(sample.map((r) => r[source])),
        isNew: true,
      },
    };
  });
}

export interface ProspectRowInput {
  name: string;
  contact_name?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  instagram?: string | null;
  city?: string | null;
  state?: string | null;
  notes?: string | null;
  tags?: string[];
  status?: ProspectStatus;
  external_id?: string | null;
  data: Record<string, unknown>;
}

export interface ImportResult {
  rows: { index: number; row: ProspectRowInput }[];
  errors: { row: number; details: string[] }[];
  /** Custom columns the plan creates (isNew), with options gathered from the data. */
  newFields: ProspectFieldDef[];
  /** Options to append to existing select columns, by key. */
  optionAdditions: Record<string, SelectOption[]>;
}

function coerceCore(key: ProspectCoreKey, raw: unknown): CoerceResult {
  if (isBlank(raw)) return { ok: true, value: null };
  switch (key) {
    case "email":
      return coerceValue({ type: "email", options: [] }, raw);
    case "website":
      return coerceValue({ type: "url", options: [] }, raw);
    case "instagram": {
      // "Redes sociais" cells often hold several profiles: prefer Instagram's.
      const tokens = asText(raw).split(/[;,\s]+/).filter(Boolean);
      const text = tokens.find((t) => /instagram\.com/i.test(t)) ?? tokens[0] ??
        "";
      const handle = text.match(/^@?([A-Za-z0-9._]{1,30})$/);
      if (handle) {
        return { ok: true, value: `https://instagram.com/${handle[1]}` };
      }
      return coerceValue({ type: "url", options: [] }, text);
    }
    case "tags": {
      const tags = splitList(raw).map((t) => t.slice(0, MAX_TAG_LENGTH));
      return { ok: true, value: tags.slice(0, MAX_TAGS) };
    }
    case "status": {
      const status = STATUS_ALIASES[normalizeHeader(asText(raw))];
      return status ? { ok: true, value: status } : { ok: true, value: null };
    }
    case "state":
      return { ok: true, value: asText(raw).slice(0, 40) };
    default:
      return coerceValue({ type: "text", options: [] }, raw);
  }
}

/**
 * Applies a column plan to source records: core fields go to columns, the
 * rest to `data`, every value coerced by its column type. A row without a
 * name is an error; a bad value drops that cell and reports it, the row
 * still imports (one typo must not lose a whole lead).
 */
export function applyColumnPlan(
  records: readonly Record<string, unknown>[],
  plan: readonly ColumnPlan[],
  existing: readonly ProspectFieldDef[],
): ImportResult {
  const fields = new Map<string, ProspectFieldDef>(
    existing.map((f) => [f.key, { ...f, options: [...f.options] }]),
  );
  const newKeys = new Set<string>();
  for (const { target } of plan) {
    if (target.kind === "field" && target.isNew && !fields.has(target.key)) {
      fields.set(target.key, {
        key: target.key,
        label: target.label,
        type: target.type,
        options: [],
      });
      newKeys.add(target.key);
    }
  }

  const rows: ImportResult["rows"] = [];
  const errors: ImportResult["errors"] = [];
  /** external_id → position in `rows`: a repeated id keeps the last row. */
  const byExternalId = new Map<string, number>();
  const seenOptions = new Map<string, string[]>();
  /** Normalized option values per select column (existing + accepted so far). */
  const optionBudget = new Map<string, Set<string>>();

  records.forEach((record, index) => {
    const row: ProspectRowInput = { name: "", data: {} };
    const details: string[] = [];
    const rowOptions: [string, string[]][] = [];
    for (const { source, target } of plan) {
      const raw = record[source];
      if (target.kind === "ignore") continue;
      if (target.kind === "core") {
        const result = coerceCore(target.key, raw);
        if (!result.ok) {
          details.push(`${source}: ${result.error}`);
        } else if (result.value !== null && result.value !== undefined) {
          (row as unknown as Record<string, unknown>)[target.key] =
            result.value;
        }
        continue;
      }
      const field = fields.get(target.key)!;
      const result = coerceValue(field, raw);
      if (!result.ok) {
        details.push(`${source}: ${result.error}`);
        continue;
      }
      if (result.value === null) continue;
      row.data[field.key] = result.value;
      if (field.type === "select" || field.type === "multi_select") {
        const values = Array.isArray(result.value)
          ? result.value as string[]
          : [result.value as string];
        const known = optionBudget.get(field.key) ??
          new Set(field.options.map((o) => normalizeHeader(o.value)));
        optionBudget.set(field.key, known);
        const fresh = values.filter((v) => !known.has(normalizeHeader(v)));
        if (
          known.size + new Set(fresh.map(normalizeHeader)).size >
            MAX_SELECT_OPTIONS
        ) {
          delete row.data[field.key];
          details.push(`${source}: coluna já tem ${MAX_SELECT_OPTIONS} opções`);
          continue;
        }
        for (const v of fresh) known.add(normalizeHeader(v));
        rowOptions.push([
          field.key,
          Array.isArray(result.value)
            ? result.value as string[]
            : [result.value as string],
        ]);
      }
    }
    if (!row.name) {
      errors.push({
        row: index + 1,
        details: ["sem nome (coluna Nome/Empresa vazia)", ...details],
      });
      return;
    }
    row.name = row.name.slice(0, 255);
    // Options only from rows that import: a rejected row adds nothing.
    for (const [key, values] of rowOptions) {
      seenOptions.set(key, [...(seenOptions.get(key) ?? []), ...values]);
    }
    if (details.length) errors.push({ row: index + 1, details });
    const previous = row.external_id
      ? byExternalId.get(row.external_id)
      : undefined;
    if (previous !== undefined) {
      errors.push({
        row: rows[previous].index + 1,
        details: [`ID externo repetido na linha ${index + 1}: vale a última`],
      });
      rows[previous] = { index, row };
      return;
    }
    if (row.external_id) byExternalId.set(row.external_id, rows.length);
    rows.push({ index, row });
  });

  const newFields: ProspectFieldDef[] = [];
  const optionAdditions: Record<string, SelectOption[]> = {};
  for (const field of fields.values()) {
    const values = seenOptions.get(field.key) ?? [];
    if (newKeys.has(field.key)) {
      newFields.push({ ...field, options: ensureOptions([], values) });
    } else if (values.length) {
      const before = field.options.length;
      const merged = ensureOptions(field.options, values);
      if (merged.length > before) {
        optionAdditions[field.key] = merged.slice(before);
      }
    }
  }
  return { rows, errors, newFields, optionAdditions };
}

/** Size limits of one import (also enforced by the DB field cap). Empty = ok. */
export function importLimitErrors(
  columnCount: number,
  plan: readonly ColumnPlan[],
  existingFieldCount: number,
): string[] {
  const errors: string[] = [];
  if (columnCount > MAX_IMPORT_COLUMNS) {
    errors.push(
      `máximo ${MAX_IMPORT_COLUMNS} colunas por importação (recebidas ${columnCount})`,
    );
  }
  const created =
    plan.filter((p) => p.target.kind === "field" && p.target.isNew).length;
  if (created > MAX_NEW_FIELDS_PER_IMPORT) {
    errors.push(
      `máximo ${MAX_NEW_FIELDS_PER_IMPORT} colunas novas por importação (seriam ${created}); ignore colunas ou use auto_create_fields=false`,
    );
  }
  if (existingFieldCount + created > MAX_FIELDS_PER_LIST) {
    errors.push(`a lista passaria de ${MAX_FIELDS_PER_LIST} colunas`);
  }
  return errors;
}
